import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { Iconify } from '@/shared/components/iconify';

// The strict icon-name union accepted by <Iconify> — only registered glyphs.
type IconName = ComponentProps<typeof Iconify>['icon'];

import type { ChatMessage } from '../data/types';

import { useShareConversation } from '../hooks/use-share-conversation';
import { markdownToPlainText } from './markdown-content';

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  /** The conversation being shared. Null ⇒ dialog stays inert (caller gates). */
  conversationId: string | null;
  /** Shown as the dialog subtitle. */
  conversationTitle: string;
  /**
   * Messages of the open conversation — used to build the Markdown/PDF export.
   * The public share page fetches its own copy; this is the local snapshot.
   */
  messages?: ChatMessage[];
}

type ShareTab = 'link' | 'qr' | 'export';

export function ShareDialog({
  open,
  onClose,
  conversationId,
  conversationTitle,
  messages = [],
}: ShareDialogProps) {
  const { t } = useTranslation('chat');
  const { share, isPending, create, regenerate, setAccess } = useShareConversation(conversationId);

  const [tab, setTab] = useState<ShareTab>('link');

  // Auto-create guard: the link is created exactly once per open. A ref keyed
  // by conversationId survives React StrictMode's double-invoke and re-renders,
  // so `create()` fires a single time even though the effect may run twice.
  const autoCreatedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!open || !conversationId) return;
    if (autoCreatedFor.current === conversationId) return;
    autoCreatedFor.current = conversationId;
    void create().catch(() => {
      // Allow a retry on the next open if the auto-create failed.
      autoCreatedFor.current = null;
    });
  }, [open, conversationId, create]);

  // Reset transient UI when the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setTab('link');
      autoCreatedFor.current = null;
    }
  }, [open]);

  const slug = share?.shareSlug ?? null;
  const shareUrl = slug ? `${window.location.origin}/share/${slug}` : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: (theme) => `${theme.shape.borderRadius}px`, maxWidth: 420 } },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header — title + close */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5 }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.primary' }}>
            {t('shareDialog.title')}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('shareDialog.close')}
            sx={{ color: 'text.secondary', marginInlineEnd: -0.5 }}
          >
            <Iconify icon="mingcute:close-line" width={18} />
          </IconButton>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v: ShareTab) => setTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            borderBottom: 1,
            borderBottomColor: 'divider',
            '& .MuiTab-root': { minHeight: 40, fontSize: 13, fontWeight: 600, textTransform: 'none' },
          }}
        >
          <Tab value="link" label={t('shareDialog.tabs.link')} />
          <Tab value="qr" label={t('shareDialog.tabs.qr')} />
          <Tab value="export" label={t('shareDialog.tabs.export')} />
        </Tabs>

        <Box sx={{ px: 3, py: 2.5, minHeight: 188 }}>
          {tab === 'link' && (
            <LinkTab url={shareUrl} title={conversationTitle} pending={isPending && !slug} />
          )}
          {tab === 'qr' && <QrTab url={shareUrl} slug={slug} />}
          {tab === 'export' && (
            <ExportTab title={conversationTitle} messages={messages} />
          )}
        </Box>

        {/* Footer — status + access switch + new-link */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            py: 1.75,
            borderTop: 1,
            borderTopColor: 'divider',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={(theme) => ({
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: share?.isShared
                  ? theme.palette.success.main
                  : theme.palette.text.disabled,
              })}
            />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              {share?.isShared ? t('shareDialog.linkLive') : t('shareDialog.linkOff')}
              {share ? ` · ${t('shareDialog.viewsCount', { count: share.shareViewCount })}` : ''}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title={t('shareDialog.newLink')}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  onClick={() => void regenerate()}
                  disabled={isPending || !slug}
                  aria-label={t('shareDialog.newLink')}
                  sx={{ color: 'text.secondary' }}
                >
                  <Iconify icon="custom:refresh-outline" width={15} />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={t('shareDialog.accessLabel')}>
              <Switch
                size="small"
                checked={share?.isShared ?? false}
                disabled={isPending || !slug}
                onChange={(e) => void setAccess(e.target.checked)}
                inputProps={{ 'aria-label': t('shareDialog.accessLabel') }}
              />
            </Tooltip>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Link tab ----------

// Social share-intent endpoints — see SHARE-FEATURE-SPEC.txt. Each opens a
// prefilled compose window in a new tab.
const SOCIALS: Array<{
  key: string;
  icon: IconName;
  url: (u: string, title: string) => string;
}> = [
  { key: 'x', icon: 'custom:social-x', url: (u) => `https://twitter.com/intent/tweet?url=${u}` },
  {
    key: 'linkedin',
    icon: 'custom:social-linkedin',
    url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
  },
  {
    key: 'reddit',
    icon: 'custom:social-reddit',
    url: (u, title) => `https://www.reddit.com/submit?url=${u}&title=${title}`,
  },
  { key: 'whatsapp', icon: 'custom:social-whatsapp', url: (u) => `https://wa.me/?text=${u}` },
  { key: 'telegram', icon: 'custom:social-telegram', url: (u) => `https://t.me/share/url?url=${u}` },
];

function LinkTab({ url, title, pending }: { url: string; title: string; pending: boolean }) {
  const { t } = useTranslation('chat');
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!url || !navigator.clipboard) return;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }, [url]);

  const openSocial = useCallback(
    (build: (u: string, title: string) => string) => {
      if (!url) return;
      window.open(
        build(encodeURIComponent(url), encodeURIComponent(title)),
        '_blank',
        'noopener,noreferrer',
      );
    },
    [url, title],
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          backgroundColor: 'background.default',
          pl: 1.5,
          pr: 0.5,
        }}
      >
        <InputBase
          value={pending && !url ? t('shareDialog.generating') : url}
          readOnly
          fullWidth
          dir="ltr"
          inputProps={{ 'aria-label': t('shareDialog.linkFieldLabel') }}
          sx={{ fontSize: 13, py: 1, color: 'text.primary' }}
        />
        <Tooltip title={copied ? t('shareDialog.copied') : t('shareDialog.copy')}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <IconButton
              size="small"
              onClick={handleCopy}
              disabled={!url}
              aria-label={t('shareDialog.copy')}
              sx={(theme) => ({
                color: copied ? theme.palette.success.main : 'text.secondary',
              })}
            >
              <Iconify icon={copied ? 'eva:checkmark-fill' : 'solar:copy-bold'} width={16} />
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>

      <Box>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.25 }}>
          {t('shareDialog.shareVia')}
        </Typography>
        <Stack direction="row" justifyContent="space-between" sx={{ gap: 0.5 }}>
          {SOCIALS.map((s) => (
            <Stack key={s.key} alignItems="center" spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              <IconButton
                onClick={() => openSocial(s.url)}
                disabled={!url}
                aria-label={t(`shareDialog.socials.${s.key}`)}
                sx={(theme) => ({
                  width: 48,
                  height: 48,
                  color: theme.palette.background.paper,
                  backgroundColor: theme.palette.text.primary,
                  transition: theme.transitions.create('opacity'),
                  '&:hover': { backgroundColor: theme.palette.text.primary, opacity: 0.85 },
                })}
              >
                <Iconify icon={s.icon} width={18} />
              </IconButton>
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                {t(`shareDialog.socials.${s.key}`)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

// ---------- QR tab ----------

function QrTab({ url, slug }: { url: string; slug: string | null }) {
  const { t } = useTranslation('chat');
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(url, { width: 320, margin: 1 })
      .then((png) => {
        if (!cancelled) setDataUrl(png);
      })
      .catch(() => {
        if (!cancelled) setDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const handleDownload = useCallback(() => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `sahib-share-${slug ?? 'qr'}.png`;
    a.click();
  }, [dataUrl, slug]);

  return (
    <Stack alignItems="center" spacing={1.75}>
      <Box
        sx={{
          width: 168,
          height: 168,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 1,
          borderColor: 'divider',
          borderRadius: (theme) => `${theme.shape.borderRadius}px`,
          backgroundColor: '#fff',
        }}
      >
        {dataUrl ? (
          <Box
            component="img"
            src={dataUrl}
            alt={t('shareDialog.qrAlt')}
            sx={{ width: 152, height: 152 }}
          />
        ) : (
          <CircularProgress size={22} />
        )}
      </Box>
      <SoftButton
        icon="solar:copy-bold"
        label={t('shareDialog.downloadPng')}
        onClick={handleDownload}
        disabled={!dataUrl}
      />
    </Stack>
  );
}

// ---------- Export tab ----------

function ExportTab({ title, messages }: { title: string; messages: ChatMessage[] }) {
  const { t } = useTranslation('chat');

  const markdown = useMemo(() => buildMarkdown(title, messages), [title, messages]);

  const handleMarkdown = useCallback(() => {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `${slugifyFilename(title)}.md`;
    a.click();
    URL.revokeObjectURL(objectUrl);
  }, [markdown, title]);

  return (
    <Stack spacing={1.5}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
        {t('shareDialog.exportHint')}
      </Typography>
      <SoftButton
        icon="custom:book-outline"
        label={t('shareDialog.exportMarkdown')}
        onClick={handleMarkdown}
        disabled={messages.length === 0}
      />
    </Stack>
  );
}

function SoftButton({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        width: '100%',
        px: 1.75,
        py: 1.25,
        border: 1,
        borderColor: 'divider',
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: 'background.default',
        color: 'text.primary',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        textAlign: 'start',
        '&:hover:not(:disabled)': {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        },
        '&:disabled': { opacity: 0.5, cursor: 'default' },
      })}
    >
      <Iconify icon={icon} width={16} />
      {label}
    </Box>
  );
}

// ---------- export builders ----------

// Serializes a conversation to a Markdown document — assistant bodies are
// flattened to plain prose (the same `markdownToPlainText` the Copy action
// uses) so the file reads cleanly regardless of the model's raw syntax.
function buildMarkdown(title: string, messages: ChatMessage[]): string {
  const lines: string[] = [`# ${title}`, ''];
  for (const m of messages) {
    const who = m.role === 'user' ? 'You' : 'Sahib';
    const body = m.role === 'assistant' ? markdownToPlainText(m.content) : m.content;
    lines.push(`**${who}:**`, '', body, '');
  }
  lines.push('---', '', '_Shared from Sahib — almanar.ai_');
  return lines.join('\n');
}

function slugifyFilename(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base || 'sahib-conversation';
}
